import React, { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { ShieldCheck, Users, Lightbulb, Check, X, UserCheck, FileText } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { VerifiedProfessionalBadge } from "@/components/Badges";

export default function Admin() {
  const { user } = useAuth();
  const [users, setUsers] = useState(null);
  const [suggestions, setSuggestions] = useState(null);
  const [verifs, setVerifs] = useState(null);

  useEffect(() => {
    if (user && user.role === "admin") {
      api.get("/admin/users").then(({ data }) => setUsers(data)).catch(() => {});
      api.get("/community-suggestions").then(({ data }) => setSuggestions(data)).catch(() => {});
      api.get("/verification-requests").then(({ data }) => setVerifs(data)).catch(() => {});
    }
  }, [user]);

  if (user === null) return <div className="p-4"><Skeleton className="h-40 w-full rounded-2xl" /></div>;
  if (!user || user.role !== "admin") return <Navigate to="/" replace />;

  const togglePro = async (u) => {
    const { data } = await api.patch(`/admin/users/${u.id}/verify-professional`);
    setUsers((list) => list.map((x) => (x.id === u.id ? { ...x, professional_verified: data.professional_verified } : x)));
    toast.success(data.professional_verified ? "Marked verified professional" : "Verification removed");
  };

  const review = async (s, action) => {
    await api.patch(`/community-suggestions/${s.id}`, null, { params: { action } });
    const status = action === "approve" ? "approved" : "rejected";
    setSuggestions((list) => list.map((x) => (x.id === s.id ? { ...x, status } : x)));
    toast.success(`Suggestion ${status}`);
  };

  const reviewVerif = async (v, action) => {
    await api.patch(`/verification-requests/${v.id}`, null, { params: { action } });
    const status = action === "approve" ? "approved" : "rejected";
    setVerifs((list) => list.map((x) => (x.id === v.id ? { ...x, status } : x)));
    if (action === "approve") {
      setUsers((list) => list && list.map((u) => (u.id === v.user_id ? { ...u, professional_verified: true } : u)));
    }
    toast.success(`Verification ${status}`);
  };

  return (
    <div className="px-4 py-4 pb-8" data-testid="admin-panel">
      <div className="mb-4 flex items-center gap-2">
        <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-navy text-white"><ShieldCheck className="h-5 w-5" /></span>
        <div>
          <h1 className="font-heading text-xl font-extrabold text-slate-900">Admin Controls</h1>
          <p className="text-xs text-slate-500">Verify professionals · curate communities</p>
        </div>
      </div>

      <Tabs defaultValue="users">
        <TabsList className="mb-3 w-full bg-slate-100">
          <TabsTrigger value="users" className="flex-1 gap-1.5 data-[state=active]:text-medorange"><Users className="h-4 w-4" /> Pros</TabsTrigger>
          <TabsTrigger value="verifs" className="flex-1 gap-1.5 data-[state=active]:text-medorange" data-testid="admin-tab-verifications"><FileText className="h-4 w-4" /> Requests</TabsTrigger>
          <TabsTrigger value="suggestions" className="flex-1 gap-1.5 data-[state=active]:text-medorange"><Lightbulb className="h-4 w-4" /> Ideas</TabsTrigger>
        </TabsList>

        <TabsContent value="users" className="space-y-2">
          {users === null && <Skeleton className="h-24 w-full rounded-xl" />}
          {users && users.map((u) => (
            <div key={u.id} className="flex items-center gap-3 rounded-xl border border-slate-200 bg-white p-3">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-1.5">
                  <p className="truncate text-sm font-bold text-slate-900">{u.name}</p>
                  {u.professional_verified && <VerifiedProfessionalBadge compact />}
                  {u.role === "admin" && <span className="rounded bg-navy px-1.5 py-0.5 text-[10px] font-bold text-white">ADMIN</span>}
                </div>
                <p className="truncate text-xs text-slate-500">{u.email} · {u.specialty || "—"}</p>
              </div>
              {u.role !== "admin" && (
                <Button size="sm" variant={u.professional_verified ? "outline" : "default"}
                  onClick={() => togglePro(u)}
                  className={u.professional_verified ? "" : "bg-sky-600 hover:bg-sky-700"}>
                  <UserCheck className="mr-1 h-3.5 w-3.5" />{u.professional_verified ? "Unverify" : "Verify Pro"}
                </Button>
              )}
            </div>
          ))}
        </TabsContent>

        <TabsContent value="verifs" className="space-y-2">
          {verifs === null && <Skeleton className="h-24 w-full rounded-xl" />}
          {verifs && verifs.length === 0 && <p className="py-8 text-center text-sm text-slate-400">No verification requests yet.</p>}
          {verifs && verifs.map((v) => (
            <div key={v.id} className="rounded-xl border border-slate-200 bg-white p-3" data-testid="verification-request-card">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <p className="font-heading text-sm font-bold text-slate-900">{v.full_name}</p>
                  <p className="text-xs text-slate-500">{v.user_email}</p>
                </div>
                <span className={`rounded px-2 py-0.5 text-[10px] font-bold uppercase ${v.status === "pending" ? "bg-amber-100 text-amber-700" : v.status === "approved" ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>{v.status}</span>
              </div>
              <div className="mt-1 space-y-0.5 text-xs text-slate-500">
                <p><strong>Specialty:</strong> {v.specialty}</p>
                <p><strong>Reg No:</strong> {v.registration_no}</p>
                {v.hospital && <p><strong>Hospital:</strong> {v.hospital}</p>}
                {v.document_url && <a href={fileUrl(v.document_url)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-medorange"><FileText className="h-3 w-3" /> View document</a>}
              </div>
              {v.status === "pending" && (
                <div className="mt-2 flex gap-2">
                  <Button size="sm" onClick={() => reviewVerif(v, "approve")} className="flex-1 bg-sky-600 hover:bg-sky-700" data-testid={`verif-approve-${v.id}`}><Check className="mr-1 h-3.5 w-3.5" /> Approve</Button>
                  <Button size="sm" variant="outline" onClick={() => reviewVerif(v, "reject")} className="flex-1"><X className="mr-1 h-3.5 w-3.5" /> Reject</Button>
                </div>
              )}
            </div>
          ))}
        </TabsContent>

        <TabsContent value="suggestions" className="space-y-2">
          {suggestions === null && <Skeleton className="h-24 w-full rounded-xl" />}
          {suggestions && suggestions.length === 0 && <p className="py-8 text-center text-sm text-slate-400">No suggestions yet.</p>}
          {suggestions && suggestions.map((s) => (
            <div key={s.id} className="rounded-xl border border-slate-200 bg-white p-3">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <p className="font-heading text-sm font-bold text-slate-900">{s.name}</p>
                  <p className="text-xs text-slate-500">by {s.suggested_by_name}</p>
                </div>
                <span className={`rounded px-2 py-0.5 text-[10px] font-bold uppercase ${s.status === "pending" ? "bg-amber-100 text-amber-700" : s.status === "approved" ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>{s.status}</span>
              </div>
              {s.description && <p className="mt-1 text-sm text-slate-600">{s.description}</p>}
              {s.reason && <p className="mt-1 text-xs italic text-slate-400">"{s.reason}"</p>}
              {s.status === "pending" && (
                <div className="mt-2 flex gap-2">
                  <Button size="sm" onClick={() => review(s, "approve")} className="flex-1 bg-emerald-600 hover:bg-emerald-700"><Check className="mr-1 h-3.5 w-3.5" /> Approve</Button>
                  <Button size="sm" variant="outline" onClick={() => review(s, "reject")} className="flex-1"><X className="mr-1 h-3.5 w-3.5" /> Reject</Button>
                </div>
              )}
            </div>
          ))}
        </TabsContent>
      </Tabs>
    </div>
  );
}
